import { ReactNode, useMemo } from 'react';
import { useTranslate } from '@tolgee/react';
import { Badge, TableColumnType } from 'antd';
import ScrollTable, { ScrollTableProps } from 'components/ScrollTable';
import { useQaContext } from 'views/Aide/Qa/Context';
import toLowerCase from 'utils/toLowerCase';
import { platformLabelMap } from 'services/config/platform';
import { useNonPlatformQaEnable } from 'hooks/business/useOrgConfig';
import { StoreIdsPreviewer } from 'views/Aide/components/StoreIdSelect';

type TR = Store.StoreGroup;

export interface StoreGroupTableProps extends ScrollTableProps<TR> {
  storeIdMap: Record<Store.Store['id'], Store.Store>;
  storeList: Store.Store[];
  extralColums?: TableColumnType<TR>[];
}

function StoreGroupTable(props: StoreGroupTableProps) {
  const { storeIdMap, storeList, extralColums, ...otherProps } = props;

  const { t } = useTranslate();

  const nonPlatformQaEnable = useNonPlatformQaEnable();

  const { platform, region } = useQaContext();

  const columns = useMemo<TableColumnType<TR>[]>(() => {
    return [
      {
        key: 'name',
        dataIndex: 'name',
        title: t('storeGroup.店铺配置组名称'),
        width: 200,
        fixed: 'left',
        render: (name: TR['name']) => {
          return nonPlatformQaEnable || !platform
            ? name
            : `${platformLabelMap[toLowerCase(platform)]}-${t(
                `region.${toLowerCase(region)}`,
              )}: ${name}`;
        },
      },
      {
        key: 'storeIds',
        dataIndex: 'storeIds',
        title: t('storeGroup.适用店铺'),
        width: 130,
        render: (storeIds: TR['storeIds']) => {
          return (
            <StoreIdsPreviewer value={storeIds} storeMap={storeIdMap} storeDataSource={storeList} />
          );
        },
      },
      {
        key: 'status',
        dataIndex: 'status',
        title: t('storeGroup.店铺配置组状态'),
        width: 135,
        render: (status: TR['status']) => {
          const statusLabelMap: Record<TR['status'], ReactNode> = {
            VALID: <Badge color="green" text={t('storeGroup.生效中')} />,
            INVALID: <Badge color="geekblue" text={t('storeGroup.未生效')} />,
          };

          return statusLabelMap[status];
        },
      },
      ...(extralColums || []),
    ];
  }, [extralColums, nonPlatformQaEnable, platform, region, storeIdMap, storeList, t]);

  return <ScrollTable rowKey="id" columns={columns} {...otherProps} />;
}

export default StoreGroupTable;
